import React, { useEffect, useState } from "react";
import moment from "moment";
import styled from "styled-components";
import { useNavigate, useParams } from "react-router-dom";
import { faCopy, faTrashAlt } from "@fortawesome/free-regular-svg-icons";
import {
    faArrowDown,
    faArrowUp,
    faChevronDown,
    faChevronUp,
    faExclamationTriangle,
    faFileContract,
    faPlus,
} from "@fortawesome/free-solid-svg-icons";
import {
    Content,
    Card,
    CardHeader,
    CardBody,
    Row,
    Col,
    FloatInput,
    ContentLoader,
    Button,
    LoadingSpinner,
    Switch,
    Alert,
    FloatSelect,
    Table,
    Icon,
    Input,
    Select,
    FloatTextareaAutogrow,
    ButtonGroup,
} from "@webtypen/react-components";
import { Page } from "../../components/Page";
import { ApiHelper, ConfirmHelper, NumericHelper } from "../../helpers";
import { ShiftRequestStatus } from "../../components/ApplyForShiftForm";
import { ContractShiftsEditButton } from "../../components/ContractShiftsEditButton";
import { CommentsBox } from "../../components/CommentsBox";

const ShiftRequestsCountBadge = styled.div`
    position: absolute;
    bottom: 0px;
    right: 0px;
    font-size: 10px;
    line-height: 10px;
    background: ${(props) => props.theme.color.primary};
    color: #fff;
    margin-bottom: -5px;
    margin-right: -5px;
    padding: 3px 4px;
    border-radius: 3px;
    user-select: none;
`;

const defaultState = {
    number: "",
    date: "",
    customer: "",
    customer_editable: true,
    employees_visible: true,
    employees_assignable: true,
    generate_number: true,
    note_internal: "",
    shifts: [],
};

export const ContractsEdit = () => {
    const params = useParams();
    const navigate = useNavigate();
    const [isLoading, setIsLoading] = useState(true);
    const [isSaving, setIsSaving] = useState(false);
    const [isDeleting, setIsDeleting] = useState(false);
    const [isHandlingShift, setIsHandlingShift] = useState(null);
    const [data, setData] = useState(null);
    const [deletedShifts, setDeletedShifts] = useState([]);
    const [customers, setCustomers] = useState(null);
    const [employees, setEmployees] = useState(null);
    const [doubleEmployees, setDoubleEmployees] = useState(null);
    const [types, setTypes] = useState(null);
    const [typesLoading, setTypesLoading] = useState(true);
    const [toggleShifts, setToggleShifts] = useState([]);

    const onChange = (e) => {
        setData({
            ...data,
            [e.target.name]: e.target.type === "checkbox" ? e.target.checked : e.target.value,
        });
    };

    const onAddShift = () => {
        const newShifts = data.shifts && data.shifts.length > 0 ? [...data.shifts] : [];
        newShifts.push({
            _id: "new",
            date: data.date ? data.date : "",
            employee: "",
            type: "",
            begin_planned: "",
            end_planned: "",
            begin_actual: "",
            end_actual: "",
        });
        setData({
            ...data,
            shifts: newShifts,
        });
    };

    const onAddFreeShift = () => {
        const newShifts = data.shifts && data.shifts.length > 0 ? [...data.shifts] : [];
        newShifts.push({
            _id: "new",
            is_free: true,
            date: data.date ? data.date : "",
            employee_name: "",
            type: "",
            begin_planned: "",
            end_planned: "",
            begin_actual: "",
            end_actual: "",
        });
        setData({
            ...data,
            shifts: newShifts,
        });
    };

    const toggleShift = (e) => {
        const index = parseInt(e.currentTarget.getAttribute("data-index"));

        const newToggleShifts = [...toggleShifts];
        if (newToggleShifts.includes(index)) {
            newToggleShifts.splice(newToggleShifts.indexOf(index), 1);
        } else {
            newToggleShifts.push(index);
        }
        setToggleShifts(newToggleShifts);
    };

    const onShiftChange = (e) => {
        const newShifts = [...data.shifts];
        newShifts[parseInt(e.target.getAttribute("data-index"))][e.target.name] =
            e.target.type === "checked" ? e.target.checked : e.target.value;

        setData({
            ...data,
            shifts: newShifts,
        });

        if (e.target.name === "employee") {
            checkEmployees(newShifts);
        }
    };

    const onShiftCopy = (e) => {
        const index = parseInt(e.currentTarget.getAttribute("data-index"));

        const newShifts = [];
        for (let i in data.shifts) {
            newShifts.push({ ...data.shifts[i] });

            if (parseInt(i) === index) {
                newShifts.push({ ...data.shifts[i], _id: "new", employee: "", begin_actual: "", end_actual: "" });
            }
        }

        setData({
            ...data,
            shifts: newShifts,
        });
    };

    const onShiftDelete = async (e) => {
        const index = parseInt(e.currentTarget.getAttribute("data-index"));
        const status = await ConfirmHelper.confirm(
            "Soll die ausgewählte Schicht/Arbeitszeit wirklich entfernt werden?",
            { confirmAttributes: { color: "danger" } }
        );
        if (!status) {
            return;
        }

        const deletedId = data.shifts[index]._id;
        if (deletedId && deletedId.trim() !== "" && deletedId !== "new") {
            const newDeletedShifts = deletedShifts && deletedShifts.length > 0 ? [...deletedShifts] : [];
            newDeletedShifts.push(deletedId);
            setDeletedShifts(newDeletedShifts);
        }

        const newShifts = [...data.shifts];
        newShifts.splice(index, 1);
        setData({
            ...data,
            shifts: newShifts,
        });
    };

    const moveUp = (e) => {
        const index = parseInt(e.currentTarget.getAttribute("data-index"));

        const newShifts = [...data.shifts];
        const help1 = { ...newShifts[index - 1] };
        const help2 = { ...newShifts[index] };
        newShifts[index] = help1;
        newShifts[index - 1] = help2;

        setData({
            ...data,
            shifts: newShifts,
        });

        const newToggleShifts = [...toggleShifts];
        if (!newToggleShifts.includes(index)) {
            newToggleShifts.push(index);
        }
        if (!newToggleShifts.includes(index - 1)) {
            newToggleShifts.push(index - 1);
        }
        setToggleShifts(newToggleShifts);
    };

    const moveDown = (e) => {
        const index = parseInt(e.currentTarget.getAttribute("data-index"));

        const newShifts = [...data.shifts];
        const help1 = { ...newShifts[index] };
        const help2 = { ...newShifts[index + 1] };
        newShifts[index] = help2;
        newShifts[index + 1] = help1;

        setData({
            ...data,
            shifts: newShifts,
        });

        const newToggleShifts = [...toggleShifts];
        if (!newToggleShifts.includes(index)) {
            newToggleShifts.push(index);
        }
        if (!newToggleShifts.includes(index + 1)) {
            newToggleShifts.push(index + 1);
        }
        setToggleShifts(newToggleShifts);
    };

    const handleShiftRequest = async (e) => {
        const id = e.currentTarget.getAttribute("data-id");
        const type = e.currentTarget.getAttribute("data-type");
        if (!type || !id || id.trim() === "" || !data._id || data._id === "new") {
            return;
        }

        setIsHandlingShift({ _id: id, type: type });
        save(
            // onSuccess
            () => {
                ApiHelper.request("/api/contracts/handle-shift-request", {
                    _id: id,
                    shift: data._id,
                    type: type,
                })
                    .then((response) => {
                        if (response.status !== "success") {
                            ConfirmHelper.alert(
                                response.status === "error" && response.message && response.message.trim() !== ""
                                    ? response.message
                                    : "Es ist ein unerwarteter Fehler aufgetreten ..."
                            );
                        }

                        load(() => {
                            setIsHandlingShift(null);
                        });
                    })
                    .catch((e) => {
                        console.error(e);

                        ConfirmHelper.alert(
                            e && e.data && e.data.status === "error" && e.data.message && e.data.message.trim() !== ""
                                ? e.data.message
                                : "Es ist ein unerwarteter Fehler aufgetreten ..."
                        );

                        load(() => {
                            setIsHandlingShift(null);
                        });
                    });
            },

            // onError
            () => {
                ConfirmHelper.alert("Es ist ein unerwarteter Fehler beim Speichern des Auftrags aufgetreten ...");
                load(() => {
                    setIsHandlingShift(null);
                });
            }
        );
    };

    const checkEmployees = (shifts) => {
        const temp = {};
        const empl = [];
        if (shifts && shifts.length > 0) {
            for (let shift of shifts) {
                if (!shift || !shift.employee) {
                    continue;
                }

                if (temp[shift.employee]) {
                    if (!empl.includes(shift.employee)) {
                        empl.push(shift.employee);
                    }
                } else {
                    temp[shift.employee] = true;
                }
            }
        }

        setDoubleEmployees(empl && empl.length > 0 ? empl : null);
    };

    const save = (onSuccess, onError) => {
        ApiHelper.request("/api/contracts/save", {
            ...data,
            _id: params.id,
            deleted_shifts: deletedShifts,
        })
            .then((response) => {
                if (response.status === "success" && response.data && response.data._id) {
                    if (onSuccess) {
                        onSuccess(response);
                    } else {
                        navigate("/contracts");
                    }
                } else {
                    if (onError) {
                        onError(response);
                    } else {
                        ConfirmHelper.alert(
                            response.status === "error" && response.message && response.message.trim() !== ""
                                ? response.message
                                : "Es ist ein unerwarteter Fehler aufgetreten ..."
                        );
                    }
                    setIsSaving(false);
                }
            })
            .catch((e) => {
                console.error(e);

                if (onError) {
                    onError(e.data);
                } else {
                    ConfirmHelper.alert(
                        e && e.data && e.data.status === "error" && e.data.message && e.data.message.trim() !== ""
                            ? e.data.message
                            : "Es ist ein unerwarteter Fehler aufgetreten ..."
                    );
                }
                setIsSaving(false);
            });
    };

    const remove = async () => {
        const status = await ConfirmHelper.confirm("Soll der Auftrag mitsamt aller Schichten entfernt werden?", {
            confirmLabel: "Entfernen",
            confirmAttributes: { color: "danger" },
        });
        if (!status) {
            return;
        }

        setIsDeleting(true);
        ApiHelper.request("/api/contracts/remove", {
            ...data,
            _id: params.id,
        })
            .then((response) => {
                if (response.status === "success") {
                    ConfirmHelper.alert("Der Auftrag wurde erfolgreich entfernt!");
                    navigate("/contracts");
                } else {
                    ConfirmHelper.alert(
                        response && response.status === "error" && response.message && response.message.trim() !== ""
                            ? response.message
                            : "Es ist ein unerwarteter Fehler aufgetreten ..."
                    );
                    setIsDeleting(false);
                }
            })
            .catch((e) => {
                console.error(e);
                ConfirmHelper.alert(
                    e && e.data && e.data.status === "error" && e.data.message && e.data.message.trim() !== ""
                        ? e.data.message
                        : "Es ist ein unerwarteter Fehler aufgetreten ..."
                );
                setIsDeleting(false);
            });
    };

    const submit = (e) => {
        e.preventDefault();
        e.stopPropagation();

        setIsSaving(true);
        save();
    };

    const load = (callback) => {
        ApiHelper.request("/api/contracts/data", {
            _id: params.id,
        })
            .then((response) => {
                setDeletedShifts([]);
                setData(response.status === "success" && response.data && response.data._id ? response.data : null);
                setIsLoading(false);

                if (response.status === "success" && response.data && response.data.shifts) {
                    checkEmployees(response.data.shifts);
                }

                if (callback) {
                    callback(response);
                }
            })
            .catch((e) => {
                console.error(e);
                setDeletedShifts([]);
                setData(null);
                setIsLoading(false);

                if (callback) {
                    callback(e.data);
                }
            });
    };

    const loadShiftTypes = () => {
        setTypesLoading(true);
        ApiHelper.request("/api/contracts/shifts/types/list")
            .then((response) => {
                setTypes(
                    response.status === "success" && response.data && response.data.length > 0 ? response.data : null
                );
                setTypesLoading(false);
            })
            .catch((e) => {
                console.error(e);
                setTypes(null);
                setTypesLoading(false);
            });
    };

    useEffect(() => {
        ApiHelper.request("/api/customers/all").then((response) => {
            setCustomers(
                response.status === "success" && response.data && response.data.length > 0 ? response.data : null
            );
        });

        ApiHelper.request("/api/employees/all").then((response) => {
            setEmployees(
                response.status === "success" && response.data && response.data.length > 0 ? response.data : null
            );
        });

        loadShiftTypes();
    }, []);

    useEffect(() => {
        if (params.id === "new") {
            setDeletedShifts([]);
            setData({ ...defaultState });
            setIsLoading(false);
        } else {
            setIsLoading(true);
            load();
        }
    }, [params.id]);

    if (isLoading || !data) {
        return (
            <Page icon={faFileContract} title="Aufträge" back="/contracts">
                <Content>
                    {isLoading ? <ContentLoader /> : <Alert danger>Fehler beim Laden des Datensatzes ...</Alert>}
                </Content>
            </Page>
        );
    }

    let totalWorkTime = 0;
    return (
        <Page
            icon={faFileContract}
            title="Aufträge"
            back="/contracts"
            actions={[<ContractShiftsEditButton onSave={loadShiftTypes} />]}
        >
            <Content>
                <form onSubmit={submit}>
                    <Row>
                        <Col width={12}>
                            <Card className="mb-3">
                                <CardHeader>Hauptinformationen</CardHeader>
                                <CardBody>
                                    <Row>
                                        <Col width={1} className="mb-2">
                                            <FloatInput
                                                type="text"
                                                name="number"
                                                placeholder="Nummer"
                                                value={data.number && !data.generate_number ? data.number : ""}
                                                onChange={onChange}
                                                disabled={
                                                    isDeleting || isSaving || isHandlingShift || data.generate_number
                                                }
                                            />
                                        </Col>
                                        <Col width={2} className="mb-2">
                                            <FloatInput
                                                type="date"
                                                name="date"
                                                placeholder="Datum"
                                                value={data.date ? data.date : ""}
                                                onChange={onChange}
                                                disabled={isDeleting || isSaving || isHandlingShift}
                                            />
                                        </Col>
                                        <Col width={3} className="mb-2">
                                            <FloatSelect
                                                name="customer"
                                                placeholder="Kunde"
                                                value={data.customer ? data.customer : ""}
                                                onChange={onChange}
                                                disabled={isDeleting || isSaving || isHandlingShift}
                                            >
                                                <option value="">-</option>
                                                {customers && customers.length > 0
                                                    ? customers.map((customer, i) => (
                                                          <option key={i} value={customer._id}>
                                                              {customer.name}
                                                          </option>
                                                      ))
                                                    : null}
                                            </FloatSelect>
                                        </Col>
                                        <Col width={6} className="mb-2">
                                            <FloatInput
                                                type="text"
                                                name="customer_contract_number"
                                                placeholder="Auftragsnr. Kunde"
                                                value={
                                                    data.customer_contract_number ? data.customer_contract_number : ""
                                                }
                                                onChange={onChange}
                                                disabled={isDeleting || isSaving || isHandlingShift}
                                            />
                                        </Col>
                                        <Col width={12}>
                                            <Row>
                                                <Col width={6}>
                                                    <FloatInput
                                                        type="text"
                                                        name="address_street"
                                                        placeholder="Straße + Hausnr."
                                                        value={data.address_street ? data.address_street : ""}
                                                        onChange={onChange}
                                                        disabled={isDeleting || isSaving || isHandlingShift}
                                                    />
                                                </Col>
                                                <Col width={2}>
                                                    <FloatInput
                                                        type="text"
                                                        name="address_zip"
                                                        placeholder="PLZ"
                                                        value={data.address_zip ? data.address_zip : ""}
                                                        onChange={onChange}
                                                        disabled={isDeleting || isSaving || isHandlingShift}
                                                    />
                                                </Col>
                                                <Col width={4}>
                                                    <FloatInput
                                                        type="text"
                                                        name="address_city"
                                                        placeholder="Stadt"
                                                        value={data.address_city ? data.address_city : ""}
                                                        onChange={onChange}
                                                        disabled={isDeleting || isSaving || isHandlingShift}
                                                    />
                                                </Col>
                                            </Row>
                                        </Col>
                                        <Col width={6}>
                                            <Row>
                                                <Col
                                                    width={12}
                                                    style={{ display: "flex", alignItems: "center" }}
                                                    className="mt-2"
                                                >
                                                    <Switch
                                                        name="customer_editable"
                                                        checked={data.customer_editable ? true : false}
                                                        onChange={onChange}
                                                        disabled={isDeleting || isSaving || isHandlingShift}
                                                        className="mr-2"
                                                    />
                                                    Darf vom Kunden bearbeitet werden
                                                </Col>
                                                <Col
                                                    width={12}
                                                    style={{ display: "flex", alignItems: "center" }}
                                                    className="mt-2"
                                                >
                                                    <Switch
                                                        name="employees_visible"
                                                        checked={data.employees_visible ? true : false}
                                                        onChange={onChange}
                                                        disabled={isDeleting || isSaving || isHandlingShift}
                                                        className="mr-2"
                                                    />
                                                    Auftrag ist für Mitarbeiter sichtbar
                                                </Col>
                                                <Col
                                                    width={12}
                                                    style={{ display: "flex", alignItems: "center" }}
                                                    className="mt-2"
                                                >
                                                    <Switch
                                                        name="employees_assignable"
                                                        checked={
                                                            data.employees_visible && data.employees_assignable
                                                                ? true
                                                                : false
                                                        }
                                                        onChange={onChange}
                                                        disabled={isDeleting || isSaving || !data.employees_visible}
                                                        className="mr-2"
                                                    />
                                                    <span style={{ opacity: data.employees_visible ? 1 : 0.5 }}>
                                                        Mitarbeiter können sich für diesen Auftrag selber eintragen
                                                    </span>
                                                </Col>
                                                {!data.number || data.number.toString().trim() === "" ? (
                                                    <Col
                                                        width={12}
                                                        style={{ display: "flex", alignItems: "center" }}
                                                        className="mt-2"
                                                    >
                                                        <Switch
                                                            name="generate_number"
                                                            checked={data.generate_number ? true : false}
                                                            onChange={onChange}
                                                            disabled={
                                                                isDeleting ||
                                                                isSaving ||
                                                                isHandlingShift ||
                                                                !data._id ||
                                                                data._id === "new"
                                                            }
                                                            className="mr-2"
                                                        />
                                                        Neue Auftragsnummer generieren
                                                    </Col>
                                                ) : null}
                                            </Row>
                                        </Col>
                                        <Col width={6} className="mt-3">
                                            <FloatTextareaAutogrow
                                                placeholder="Notiz für Mitarbeiter"
                                                name="note_internal"
                                                value={data.note_internal ? data.note_internal : ""}
                                                onChange={(e) => setData({ ...data, note_internal: e.target.value })}
                                                disabled={isDeleting || isSaving || isHandlingShift}
                                            />
                                            <FloatTextareaAutogrow
                                                placeholder="Gäste-Info"
                                                className="mt-2"
                                                name="guests"
                                                value={data.guests ? data.guests : ""}
                                                onChange={(e) => setData({ ...data, guests: e.target.value })}
                                                disabled={isDeleting || isSaving || isHandlingShift}
                                            />
                                        </Col>
                                    </Row>
                                </CardBody>
                            </Card>
                        </Col>

                        <Col width={12}>
                            <Card className="mb-3">
                                <CardHeader>
                                    <Button
                                        type="button"
                                        onClick={onAddShift}
                                        disabled={isDeleting || isSaving || isHandlingShift}
                                        sm
                                        light
                                        className="float-right"
                                        style={{ marginTop: -5 }}
                                    >
                                        <Icon icon={faPlus} className="mr-1" />
                                        Hinzufügen
                                    </Button>
                                    <Button
                                        type="button"
                                        onClick={onAddFreeShift}
                                        disabled={isDeleting || isSaving || isHandlingShift}
                                        sm
                                        light
                                        className="float-right mr-2"
                                        style={{ marginTop: -5 }}
                                    >
                                        <Icon icon={faPlus} className="mr-1" />
                                        Freie hinzufügen
                                    </Button>
                                    Schichten / Arbeitszeiten
                                </CardHeader>
                                <div style={{ overflow: "auto" }}>
                                    <Table className="valign-middle">
                                        <thead>
                                            <tr>
                                                <th style={{ width: 115, paddingRight: 0 }}></th>
                                                <th style={{ width: 180 }}>Datum</th>
                                                <th style={{ width: 150 }}>Von</th>
                                                <th style={{ width: 150 }}>Bis</th>
                                                <th>Mitarbeiter</th>
                                                <th style={{ width: 150 }}>Typ</th>
                                                <th style={{ width: 150 }}>Tats. Beginn</th>
                                                <th style={{ width: 150 }}>Tats. Ende</th>
                                                <th className="text-right" style={{ width: 80 }}>
                                                    Arbeitszeit
                                                </th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {!data.shifts || data.shifts.length < 1 ? (
                                                <tr>
                                                    <td colSpan={8}>
                                                        Es wurden noch keine Schichten / Arbeitszeiten zu diesem Auftrag
                                                        hinzugefügt ...
                                                    </td>
                                                </tr>
                                            ) : (
                                                data.shifts.map((el, i) => {
                                                    const isOpen = toggleShifts.includes(parseInt(i));

                                                    let duration = null;
                                                    if (el.begin_actual && el.end_actual) {
                                                        const start = moment("2023-01-01 " + el.begin_actual + ":00");
                                                        const end = moment(
                                                            (el.begin_actual <= el.end_actual
                                                                ? "2023-01-01 "
                                                                : "2023-01-02 ") +
                                                                el.end_actual +
                                                                ":00"
                                                        );

                                                        const diff = end.diff(start, "minutes");
                                                        duration = diff > 0 ? diff / 60 : null;
                                                        totalWorkTime += parseFloat(parseFloat(duration).toFixed(2));
                                                    }

                                                    return (
                                                        <React.Fragment key={i}>
                                                            <tr>
                                                                <td className="pr-0">
                                                                    <Button
                                                                        type="button"
                                                                        data-index={i}
                                                                        onClick={onShiftDelete}
                                                                        disabled={
                                                                            isDeleting || isSaving || isHandlingShift
                                                                        }
                                                                        sm
                                                                        danger
                                                                    >
                                                                        <Icon icon={faTrashAlt} />
                                                                    </Button>
                                                                    <Button
                                                                        type="button"
                                                                        data-index={i}
                                                                        onClick={onShiftCopy}
                                                                        disabled={
                                                                            isDeleting || isSaving || isHandlingShift
                                                                        }
                                                                        className="ml-1"
                                                                        sm
                                                                        light
                                                                    >
                                                                        <Icon icon={faCopy} />
                                                                    </Button>
                                                                    <Button
                                                                        type="button"
                                                                        data-index={i}
                                                                        onClick={toggleShift}
                                                                        disabled={
                                                                            isDeleting || isSaving || isHandlingShift
                                                                        }
                                                                        sm
                                                                        light
                                                                        className="ml-1"
                                                                        style={{ position: "relative" }}
                                                                    >
                                                                        {isOpen ? (
                                                                            <Icon icon={faChevronUp} />
                                                                        ) : (
                                                                            <Icon icon={faChevronDown} />
                                                                        )}

                                                                        {el.shift_requests &&
                                                                        el.shift_requests.length > 0 ? (
                                                                            <ShiftRequestsCountBadge>
                                                                                {el.shift_requests.length}
                                                                            </ShiftRequestsCountBadge>
                                                                        ) : null}
                                                                    </Button>
                                                                </td>
                                                                <td>
                                                                    <Input
                                                                        type="date"
                                                                        name="date"
                                                                        value={el.date ? el.date : ""}
                                                                        onChange={onShiftChange}
                                                                        data-index={i}
                                                                        disabled={
                                                                            isDeleting || isSaving || isHandlingShift
                                                                        }
                                                                    />
                                                                </td>
                                                                <td>
                                                                    <Input
                                                                        type="time"
                                                                        name="begin_planned"
                                                                        value={el.begin_planned ? el.begin_planned : ""}
                                                                        onChange={onShiftChange}
                                                                        data-index={i}
                                                                        disabled={
                                                                            isDeleting || isSaving || isHandlingShift
                                                                        }
                                                                    />
                                                                </td>
                                                                <td>
                                                                    <Input
                                                                        type="time"
                                                                        name="end_planned"
                                                                        value={el.end_planned ? el.end_planned : ""}
                                                                        onChange={onShiftChange}
                                                                        data-index={i}
                                                                        disabled={
                                                                            isDeleting || isSaving || isHandlingShift
                                                                        }
                                                                    />
                                                                </td>
                                                                <td>
                                                                    <div
                                                                        style={{
                                                                            display: "flex",
                                                                            flexDirection: "row",
                                                                            alignItems: "center",
                                                                            justifyContent: "center",
                                                                        }}
                                                                    >
                                                                        {el.is_free ? (
                                                                            <div style={{ flex: 1 }}>
                                                                                <Input
                                                                                    type="text"
                                                                                    name="employee_name"
                                                                                    value={
                                                                                        el.employee_name
                                                                                            ? el.employee_name
                                                                                            : ""
                                                                                    }
                                                                                    onChange={onShiftChange}
                                                                                    data-index={i}
                                                                                    disabled={
                                                                                        isDeleting ||
                                                                                        isSaving ||
                                                                                        isHandlingShift
                                                                                    }
                                                                                />
                                                                            </div>
                                                                        ) : (
                                                                            <>
                                                                                <div style={{ flex: 1 }}>
                                                                                    <Select
                                                                                        name="employee"
                                                                                        value={
                                                                                            el.employee
                                                                                                ? el.employee
                                                                                                : ""
                                                                                        }
                                                                                        onChange={onShiftChange}
                                                                                        data-index={i}
                                                                                        disabled={
                                                                                            isDeleting ||
                                                                                            isSaving ||
                                                                                            isHandlingShift
                                                                                        }
                                                                                    >
                                                                                        <option value="">-</option>
                                                                                        {employees &&
                                                                                        employees.length > 0
                                                                                            ? employees.map(
                                                                                                  (employee, e) => (
                                                                                                      <option
                                                                                                          key={e}
                                                                                                          value={
                                                                                                              employee._id
                                                                                                          }
                                                                                                      >
                                                                                                          {
                                                                                                              employee.firstname
                                                                                                          }
                                                                                                          {employee.lastname
                                                                                                              ? (employee.lastname
                                                                                                                    ? " "
                                                                                                                    : "") +
                                                                                                                employee.lastname
                                                                                                              : ""}
                                                                                                      </option>
                                                                                                  )
                                                                                              )
                                                                                            : null}
                                                                                    </Select>
                                                                                </div>

                                                                                {doubleEmployees &&
                                                                                doubleEmployees.includes(
                                                                                    el.employee
                                                                                ) ? (
                                                                                    <div className="ml-2">
                                                                                        <Icon
                                                                                            icon={faExclamationTriangle}
                                                                                        />
                                                                                    </div>
                                                                                ) : null}
                                                                            </>
                                                                        )}
                                                                    </div>
                                                                </td>
                                                                <td>
                                                                    <Select
                                                                        name="type"
                                                                        value={el.type ? el.type : ""}
                                                                        onChange={onShiftChange}
                                                                        data-index={i}
                                                                        disabled={
                                                                            isDeleting ||
                                                                            isSaving ||
                                                                            isHandlingShift ||
                                                                            typesLoading
                                                                        }
                                                                    >
                                                                        <option value="">-</option>
                                                                        {types && types.length > 0
                                                                            ? types.map((type, t) => (
                                                                                  <option key={t} value={type._id}>
                                                                                      {type.name}
                                                                                  </option>
                                                                              ))
                                                                            : null}
                                                                    </Select>
                                                                </td>
                                                                <td>
                                                                    <Input
                                                                        type="time"
                                                                        name="begin_actual"
                                                                        value={el.begin_actual ? el.begin_actual : ""}
                                                                        onChange={onShiftChange}
                                                                        data-index={i}
                                                                        disabled={
                                                                            isDeleting || isSaving || isHandlingShift
                                                                        }
                                                                    />
                                                                </td>
                                                                <td>
                                                                    <Input
                                                                        type="time"
                                                                        name="end_actual"
                                                                        value={el.end_actual ? el.end_actual : ""}
                                                                        onChange={onShiftChange}
                                                                        data-index={i}
                                                                        disabled={
                                                                            isDeleting || isSaving || isHandlingShift
                                                                        }
                                                                    />
                                                                </td>
                                                                <td
                                                                    className="text-right"
                                                                    style={{ fontWeight: "bold" }}
                                                                >
                                                                    {duration
                                                                        ? NumericHelper.numberFormat(duration) + " h"
                                                                        : "-"}
                                                                </td>
                                                            </tr>
                                                            {isOpen ? (
                                                                <tr>
                                                                    <td
                                                                        style={{
                                                                            verticalAlign: "top",
                                                                            borderTop: "none",
                                                                            paddingTop: 0,
                                                                        }}
                                                                    >
                                                                        <Button
                                                                            type="button"
                                                                            onClick={moveUp}
                                                                            data-index={i}
                                                                            xs
                                                                            light
                                                                            block
                                                                            disabled={
                                                                                isDeleting ||
                                                                                isSaving ||
                                                                                isHandlingShift ||
                                                                                i === 0
                                                                            }
                                                                        >
                                                                            <Icon icon={faArrowUp} className="mr-1" />
                                                                            Hoch
                                                                        </Button>
                                                                        <Button
                                                                            type="button"
                                                                            onClick={moveDown}
                                                                            data-index={i}
                                                                            xs
                                                                            light
                                                                            block
                                                                            className="mt-1"
                                                                            disabled={
                                                                                isDeleting ||
                                                                                isSaving ||
                                                                                isHandlingShift ||
                                                                                i === data.shifts.length - 1
                                                                            }
                                                                        >
                                                                            <Icon icon={faArrowDown} className="mr-1" />
                                                                            Runter
                                                                        </Button>
                                                                    </td>
                                                                    <td
                                                                        style={{ borderTop: "none", paddingTop: 0 }}
                                                                        colSpan={6}
                                                                    >
                                                                        <Card>
                                                                            <CardHeader>
                                                                                Bewerbungen für diese Schicht
                                                                            </CardHeader>
                                                                            {!el.shift_requests ||
                                                                            el.shift_requests.length < 1 ? (
                                                                                <CardBody>
                                                                                    Es gibt noch keine Bewerbungen für
                                                                                    diese Schicht
                                                                                </CardBody>
                                                                            ) : (
                                                                                <Table>
                                                                                    <tbody>
                                                                                        {el.shift_requests.map(
                                                                                            (shiftRequest, r) => (
                                                                                                <tr key={r}>
                                                                                                    <td
                                                                                                        style={{
                                                                                                            width: 100,
                                                                                                        }}
                                                                                                    >
                                                                                                        <ShiftRequestStatus
                                                                                                            status={
                                                                                                                shiftRequest.status
                                                                                                            }
                                                                                                        />
                                                                                                    </td>
                                                                                                    <td
                                                                                                        style={{
                                                                                                            width: 200,
                                                                                                        }}
                                                                                                    >
                                                                                                        {
                                                                                                            shiftRequest
                                                                                                                .employee_object
                                                                                                                .lastname
                                                                                                        }
                                                                                                        {shiftRequest
                                                                                                            .employee_object
                                                                                                            .firstname
                                                                                                            ? ", " +
                                                                                                              shiftRequest
                                                                                                                  .employee_object
                                                                                                                  .firstname
                                                                                                            : ""}
                                                                                                        <div
                                                                                                            style={{
                                                                                                                fontSize: 10,
                                                                                                            }}
                                                                                                        >
                                                                                                            {moment(
                                                                                                                shiftRequest.created_at
                                                                                                            ).fromNow()}
                                                                                                        </div>
                                                                                                    </td>
                                                                                                    <td>
                                                                                                        {
                                                                                                            shiftRequest.note
                                                                                                        }
                                                                                                    </td>
                                                                                                    <td className="text-right">
                                                                                                        {shiftRequest.status ===
                                                                                                        "pending" ? (
                                                                                                            <ButtonGroup>
                                                                                                                <Button
                                                                                                                    type="button"
                                                                                                                    sm
                                                                                                                    success
                                                                                                                    onClick={
                                                                                                                        handleShiftRequest
                                                                                                                    }
                                                                                                                    data-id={
                                                                                                                        shiftRequest._id
                                                                                                                    }
                                                                                                                    data-type="confirm"
                                                                                                                    disabled={
                                                                                                                        isDeleting ||
                                                                                                                        isSaving ||
                                                                                                                        isHandlingShift
                                                                                                                    }
                                                                                                                >
                                                                                                                    {isHandlingShift &&
                                                                                                                    isHandlingShift._id ===
                                                                                                                        shiftRequest._id &&
                                                                                                                    isHandlingShift.type ===
                                                                                                                        "confirm" ? (
                                                                                                                        <LoadingSpinner />
                                                                                                                    ) : (
                                                                                                                        "Annehmen"
                                                                                                                    )}
                                                                                                                </Button>
                                                                                                                <Button
                                                                                                                    type="button"
                                                                                                                    sm
                                                                                                                    danger
                                                                                                                    onClick={
                                                                                                                        handleShiftRequest
                                                                                                                    }
                                                                                                                    data-id={
                                                                                                                        shiftRequest._id
                                                                                                                    }
                                                                                                                    data-type="decline"
                                                                                                                    disabled={
                                                                                                                        isDeleting ||
                                                                                                                        isSaving ||
                                                                                                                        isHandlingShift
                                                                                                                    }
                                                                                                                >
                                                                                                                    {isHandlingShift &&
                                                                                                                    isHandlingShift._id ===
                                                                                                                        shiftRequest._id &&
                                                                                                                    isHandlingShift.type ===
                                                                                                                        "decline" ? (
                                                                                                                        <LoadingSpinner />
                                                                                                                    ) : (
                                                                                                                        "Ablehnen"
                                                                                                                    )}
                                                                                                                </Button>
                                                                                                            </ButtonGroup>
                                                                                                        ) : null}
                                                                                                    </td>
                                                                                                </tr>
                                                                                            )
                                                                                        )}
                                                                                    </tbody>
                                                                                </Table>
                                                                            )}
                                                                        </Card>
                                                                    </td>
                                                                </tr>
                                                            ) : null}
                                                        </React.Fragment>
                                                    );
                                                })
                                            )}

                                            {data.shifts && data.shifts.length > 1 ? (
                                                <tr>
                                                    <td colSpan={7}></td>
                                                    <td
                                                        className="text-right"
                                                        style={{ fontWeight: "bold", borderTop: "2px solid #333" }}
                                                    >
                                                        {totalWorkTime
                                                            ? NumericHelper.numberFormat(totalWorkTime) + " h"
                                                            : "-"}
                                                    </td>
                                                </tr>
                                            ) : null}
                                        </tbody>
                                    </Table>
                                </div>
                            </Card>
                        </Col>

                        {employees && employees.length > 0 && doubleEmployees && doubleEmployees.length > 0 ? (
                            <Col width={12}>
                                <Alert warning>
                                    <b>Mitarbeiter doppelt eingeteilt:</b>
                                    <br />
                                    {doubleEmployees.map((emplId, i) => {
                                        const empl = employees.find((el) => el._id === emplId);
                                        return <div>{empl ? empl.firstname + " " + empl.lastname : "???"}</div>;
                                    })}
                                </Alert>
                            </Col>
                        ) : null}

                        <Col width={12} className="text-right">
                            {params.id !== "new" ? (
                                <Button
                                    type="button"
                                    className="mr-2"
                                    danger
                                    onClick={remove}
                                    disabled={isSaving || isHandlingShift || isDeleting}
                                >
                                    {isDeleting ? <LoadingSpinner /> : "Entfernen"}
                                </Button>
                            ) : null}

                            <Button disabled={isDeleting || isSaving || isHandlingShift}>
                                {isSaving ? <LoadingSpinner /> : "Speichern"}
                            </Button>
                        </Col>
                    </Row>
                </form>

                <Row>
                    <Col width={6}>
                        {params.id !== "new" ? (
                            <CommentsBox title="Kommentare" channel={"contracts_" + params.id} />
                        ) : null}
                    </Col>
                </Row>
            </Content>
        </Page>
    );
};
